import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { MSelect, MSlider } from "components/Shared";
import {
  DayRangePlusMinus45,
  DayRange0To20,
  StarRating,
  CountTen,
  RangeZeroToOneHundred,
  RangeZeroToTwoHundred,
  HourRangeZeroToFortyEight,
  WeekRange0To16,
} from "typeConstants/mSliderRanges";
import {
  yesNo,
  appraisalStatus,
  testAndTrainingAccounts,
  webStatus,
  hazards,
  businessTypes,
  activeInactive,
  inspectionClass,
  customerTypeCode,
  inspectorHiringForecastStatus,
  feedbackCategories,
  inspectorSpecificCodes,
  simpleDaysDropdown2,
  simpleDaysDropdown,
  simpleTimeDropdown,
  simpleTimeDropdown2,
  appraisalPortStatusGroups,
  filterTypes,
  feedbackResultsOptions,
  lowMediumHighTiers,
  cubiCasaStatus,
} from "typeConstants/quicklistFilters";
import {
  stateListSelector,
  appraiserManagersSelector,
  customerServiceAssignToUsersSelector,
  underwritersSelector,
  busOpsDirectorsSelector,
  busOpsManagersSelector,
  appraisersSelector,
  customerActionsSelector,
  allCountiesSelector,
  allFieldDevelopmentManagersSelector,
  allLenderLogisticsManagersSelector,
  serviceRegionSelector,
  fieldOperationsManagersSelector,
  allCustomerExperienceManagersSelector,
  allCustomerExperienceDirectorsSelector,
} from "selectors/menuItems";
import {operatorNames} from "components/Homeowner/Dashboard/QuickListConfigOperatorNames";
import {getCustomerStackCodeByLogin} from "requests/stackCodeSettings";
import { CustomerServiceDeptOptions } from "typeConstants/employeeDepartments.ts";

const getCustomerListByTemplate = (filterMenus, activeTemplate) => {
  switch (activeTemplate) {
    case "AppraisalCases":
      return filterMenus.lenderCustomerList;
    default:
      return filterMenus.insuranceCustomerList; // this includes lender customers... don't ask why
  }
};

const getCustomerFilter = (filterMenus, activeTemplate) => {
  // get the filter based on template
  const list = getCustomerListByTemplate(filterMenus, activeTemplate);
  if (filterMenus && Array.isArray(list) && list.length) {
    let filtered = [];
    list.forEach(cust => {
      let matches = cust.match(/.*\(([^)]+)\)/);
      if (matches) {
        filtered.push({ value: matches[matches.length - 1], label: cust });
      }
    });
    return filtered;
  }
  return [];
};

const getStackCodeFilter = (mounted) => {
  let filtered = [];
  getCustomerStackCodeByLogin((data) => {
    data.forEach(stackCode => {
      filtered.push({ value: stackCode.code, label: `${stackCode.code}: ${stackCode.name}`});
    });
  }, () => {}, mounted);
  return filtered;
};

const getSurveyTypeFilter = (surveyTypes) => {
  let filtered = [];
  if (surveyTypes && Array.isArray(surveyTypes) && surveyTypes.length) {
    surveyTypes.forEach(survey => {
      filtered.push({ value: survey.id.toString(), label: survey.name });
    });
  }
  return filtered;
};

const getAssignmentTypes = (filterMenus) => {
  let filtered = [];
  if (filterMenus && Array.isArray(filterMenus.assignmentTypes) && filterMenus.assignmentTypes.length) {
    filterMenus.assignmentTypes.forEach(assignmentType => {
      filtered.push({ value: assignmentType, label: assignmentType });
    });
  }
  return filtered;
};

const getFieldLevelCoordinators = ({ fieldLevelCoordinators }) =>
  (fieldLevelCoordinators || []).map(({ employeeId, name }) => ({
    value: employeeId.toString(),
    label: name,
  }));

const getFieldDirectors = ({ fieldDirectors }) => {
  return (fieldDirectors || []).map(({ employeeId, name }) => ({
    value: employeeId.toString(),
    label: name,
  }));
};

// CurrentUser is replaced with InternetUserId on the backend
const getCurrentUserFilter = () => {
  return [{ value: "[CurrentUser]", label: "{ Me }" }];
};

// CurrentUnderwriter is replaced with underwriter code on the backend
const getCurrentUnderwriterFilter = () => {
  return [{ value: "[CurrentUnderwriter]", label: "{ Me }" }];
};

const getAppraiserSupportReps = ({ appraiserSupportRepresentatives }) =>
  (appraiserSupportRepresentatives || []).map(({ employeeId, name }) => ({
    value: employeeId,
    label: name,
  }));

const getValuationDirectors = ({ valuationDirectors }) =>
  (valuationDirectors || []).map(({ employeeId, name }) => ({
    value: employeeId,
    label: name,
  }));

const getAppraisalQualityAssuranceAgents = ({ appraisalQualityAssuranceAgents }) =>
  (appraisalQualityAssuranceAgents || []).map(({ internetUserId, name }) => ({
    value: internetUserId,
    label: name,
  }));

const shapeEmployeesList = (employeeArray) =>
  (employeeArray || []).map(({ internetUserId, firstName, lastName }) => ({
    value: internetUserId,
    label: `${firstName} ${lastName}`,
  }));

const shapeExportTypeList = (exportTypes) =>
  (exportTypes || []).map(({ name, code }) => ({
    value: code,
    label: name,
}));

const getCustomerServicePersonNames = ({ customerServicePersonNames }) => (customerServicePersonNames || []).map(({ name, id }) => ({ value: id, label: name }));

const filterControls = {
  options: "options",
  singleOptions: "single-options",
  text: "text",
  rangeSlider: "rangeslider",
  slider: "slider",
};

const getFieldType = (field, operator, simpleMode, inputType) => {
  if (simpleMode && operator !== operatorNames.True && operator !== operatorNames.Text) {
    return filterControls.singleOptions;
  }

  switch (inputType) {
    case filterTypes.valuationCoordinators:
    case filterTypes.valuationDirectors:
    case filterTypes.exportType:
    case filterTypes.activeInactive:
    case filterTypes.appointmentContactEfforts:
    case filterTypes.appraisalPortStatus:
    case filterTypes.appraisalQualityAssuranceAgent:
    case filterTypes.appraisalStatus:
    case filterTypes.appraisalSupportRepresentatives:
    case filterTypes.appraiserAssigned:
    case filterTypes.appraiserManagers:
    case filterTypes.assignmentTypes:
    case filterTypes.bool:
    case filterTypes.businessTypes:
    case filterTypes.county:
    case filterTypes.customerNumbers:
    case filterTypes.customerServiceAssignUsers:
    case filterTypes.customerType:
    case filterTypes.feedbackCategory:
    case filterTypes.fieldDevelopmentManager:
    case filterTypes.fieldDirector:
    case filterTypes.fieldLevelCoordinator:
    case filterTypes.fieldQualityAssuranceAgent:
    case filterTypes.fieldStatus:
    case filterTypes.hazardLevel:
    case filterTypes.inspectorAssignedSpecificCodes:
    case filterTypes.inspectorHiringForecastStatus:
    case filterTypes.lenderLogisticsManager:
    case filterTypes.qualityAssuranceDirector:
    case filterTypes.qualityAssuranceManager:
    case filterTypes.reviewerActions:
    case filterTypes.states:
    case filterTypes.surveyClass:
    case filterTypes.surveyTypes:
    case filterTypes.testAndTrainingAccounts:
    case filterTypes.underWriterActions:
    case filterTypes.underwriterCode:
    case filterTypes.valuationDirectorsEmployeeId:
    case filterTypes.valuationDirectorsInternetUser:
    case filterTypes.yesNo:
    case filterTypes.zipCode:
    case filterTypes.serviceRegion:
    case filterTypes.fieldOperationsManager:
    case filterTypes.stackCodes:
    case filterTypes.feedbackResults:
    case filterTypes.lowMediumHighTiers:
    case filterTypes.customerServicePersonName:
    case filterTypes.fieldRepAssigned:
    case filterTypes.cubiCasaStatus:
    case filterTypes.departments:
    case filterTypes.customerExperienceManagers:
    case filterTypes.customerExperienceDirectors:
    case filterTypes.satisfactionWithSchedulingProcess:
    case filterTypes.didInspectorArriveOnTimeScore:
    case filterTypes.inspectorsProfessionalismAndCommunicationScore:
    case filterTypes.overallSatisfactionScore:
      switch (operator) {
        case operatorNames.True:
        case operatorNames.False:
          return null;
        case operatorNames.NotInList:
        case operatorNames.InList:
        default:
          return filterControls.options;
      }
    case filterTypes.text:
      return filterControls.text;
    default:
      switch (operator) {
        case operatorNames.Today:
        case operatorNames.Overdue:
        case operatorNames.Empty:
        case operatorNames.NotEmpty:
        case operatorNames.True:
        case operatorNames.False:
          return null;
        case operatorNames.Range:
          return filterControls.rangeSlider;
        default:
          return filterControls.slider;
      }
  }
};

const getSliderValue = (value) => {
  let numberValue = parseFloat(value);
  if (isNaN(numberValue)) {
    return 0;
  }
  return Math.round(numberValue * 10) / 10;
};

const getMultiValue = (selected) => {
  if (selected == undefined || selected == "") return [];
  if (Array.isArray(selected)) return selected;
  if (typeof selected == "string") { return selected.split(","); }
  return [];
};

const dropDownOneThroughFive = [
  {
    label: "1",
    value: 1,
  },
  {
    label: "2",
    value: 2,
  },
  {
    label: "3",
    value: 3,
  },
  {
    label: "4",
    value: 4,
  },
  {
    label: "5",
    value: 5,
  }
];

class ValueEditor extends React.Component {
  constructor(props) {
    super(props);
    this.mounted = { current: true };
    this.updateSlider = this.updateSlider.bind(this);
    this.multiValueRenderer = this.multiValueRenderer.bind(this);
    this.multiValueChanged = this.multiValueChanged.bind(this);
    this.MSelectValueChanged = this.MSelectValueChanged.bind(this);
    this.getAllOption = this.getAllOption.bind(this);
    this.getPlaceHolder = this.getPlaceHolder.bind(this);
    this.numberValueChanged = this.numberValueChanged.bind(this);
    this.getSlider = this.getSlider.bind(this);
    this.getFieldOptions = this.getFieldOptions.bind(this);
    this.selectRef = React.createRef(),
      this.state = {
        numberValue: 0,
        multiValue: [],
        selectOptions: [],
        selectField: "",
      };
  }

  componentDidUpdate(prevProps) {
    const { field } = this.props;
    if (prevProps.field && field && prevProps.field.value !== field.value && this.selectRef && this.selectRef.current)
      this.selectRef.current.clearValue();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // When user changes the field, wipe out the value.
    if (this.props.field !== nextProps.field) {
      this.onChangeField(nextProps.field);
    }
    const { field, operator } = this.props;
    if (operator !== nextProps.operator || field !== nextProps.field) {
      const { field } = nextProps;
      let options = this.getFieldOptions(field, nextProps, this.mounted);
      this.setState({
        selectOptions: options,
        multiValue: [],
        numberValue: 0,
      });
    }
  }

  UNSAFE_componentWillMount() {
    const { value, field, simpleMode, operator, inputType } = this.props;
    this.setState({ numberValue: getSliderValue(value) });
    this.setState({ multiValue: getMultiValue(value) });

    if (this.isFieldTypeOptions(field, operator, simpleMode, inputType)) {
      let options = this.getFieldOptions(field, this.props, this.mounted);
      this.setState({ selectOptions: options });
    }
  }

  isFieldTypeOptions(field, operator, simpleMode, inputType) {
    let fieldType = getFieldType(field, operator, simpleMode, inputType);
    return [filterControls.options, filterControls.singleOptions].some(s => s === fieldType);
  }

  onChangeField(field) {
    if (this.isFieldTypeOptions(field, this.props.operator, this.props.simpleMode, this.props.inputType) && field == this.state.selectField) {
      let options = this.getFieldOptions(field, this.props, this.mounted);
      this.setState({ selectOptions: options });
      this.props.handleOnChange("");
    }
  }

  updateSlider(value, e) {
    if (e && e.stopPropagation) e.stopPropagation();
    let newValue = getSliderValue(value);
    if (newValue !== this.state.numberValue) {
      this.setState({ numberValue: newValue });
      this.props.handleOnChange(newValue.toString());
    }
  }

  multiValueRenderer(selected, options) {
    if (selected.length === options.length) return "All Selected";
    if (selected.length) return `${selected.length} Selected`;
    return "Please select...";
  }

  multiValueChanged(selected) {
    const newMultiValue = Array.isArray(selected) ? selected : selected.split(",");
    if (newMultiValue !== this.state.multiValue) {
      this.setState({ multiValue: newMultiValue });
      this.props.handleOnChange(selected);
    }
  }

  MSelectValueChanged(selected) {
    //will be array for multiselect, single item for single select
    this.multiValueChanged(Array.isArray(selected) ? selected.map(s => s.value).join(",") : selected.value);
    let operator = (Array.isArray(selected) ? selected[0] : selected)?.operator;
    if (operator) this.props.onOperatorChanged(operator);
  }

  numberValueChanged(newNumberValue) {
    if (newNumberValue !== this.state.numberValue) {
      this.setState({ numberValue: newNumberValue });
      this.props.handleOnChange(newNumberValue.toString());
    }
  }

  getAllOption() {
    const { field } = this.props;
    switch (field) {
      case "State":
        return "All States";
      default: null;
    }
  }

  getPlaceHolder() {
    const { field } = this.props;
    switch (field) {
      case "State":
        return "Select State...";
      default: null;
    }
  }

  getSlider() {
    const { operator, inputType, className } = this.props;
    let settings;
    switch (inputType) {
      case filterTypes.date:
        settings = DayRangePlusMinus45;
        break;
      case filterTypes.surveyRating:
        settings = StarRating;
        break;
      case filterTypes.countTen:
        settings = CountTen;
        break;
      case filterTypes.rangeZeroToOneHundred:
        settings = RangeZeroToOneHundred;
        break;
      case filterTypes.rangeZeroToTwoHundred:
        settings = RangeZeroToTwoHundred;
        break;
      case filterTypes.timeHours:
        settings = HourRangeZeroToFortyEight;
        break;
      case filterTypes.timeWeeks:
        settings = WeekRange0To16;
        break;
      default:
        settings = DayRange0To20;
    }

    return (
      <MSlider
        id="rule-filter-slider"
        className={className}
        key="__mslider"
        {...settings}
        defaultValue={this.state.numberValue ? this.state.numberValue : 0}
        onChangeCallback={this.numberValueChanged}
        invertTrack={operator === operatorNames.GreaterThan && inputType === filterTypes.date ? true : false}
      />
    );
  }

  getFieldOptions(field, settings, mounted) {
    const {
      gridFilterMenus,
      stateList,
      allSurveyTypes,
      appraiserManagers,
      activeTemplate,
      customerServiceAssignUsers,
      underwriters,
      busOpsDirectors,
      busOpsManagers,
      appraisers,
      customerActions,
      simpleMode,
      valuationCoordinators,
      valuationDirectors,
      exportTypes,
      inputType,
      countyList,
      fieldDevelopmentManagers,
      lenderLogisticsManagers,
      serviceRegions,
      operationsManagers,
      customerExperienceManagers,
      customerExperienceDirectors,
    } = settings;

    if (simpleMode) {
      // Specific handling for simple mode by field name
      switch (field) {
        case "DateCompleted":
        case "DateReturned":
        case "DateEntered":
        case "DateReviewed":
          return simpleDaysDropdown;
        case "appointmentDateTime":
        case "customer_duedate":
        case "effectiveDate":
          return simpleDaysDropdown2;
        case "DaysInStatusRaw":
        case "DaysSinceUWAction":
        case "timeAssignedWithNoContactEfforts":
        case "timeInQA1":
        case "TimeSinceScheduledAppointmentRaw":
        case "timeUnassignedFieldRep":
        case "AppraiserEta":
        case "ClientEta":
          return simpleTimeDropdown;
        case "time_in_fs_over":
          return simpleTimeDropdown2;
      }
      // General handling for simple mode by inputType
      switch (inputType){
        case filterTypes.date: return simpleDaysDropdown2;
        case filterTypes.time: return simpleTimeDropdown;
      }
    }

    switch (inputType){
      case filterTypes.valuationCoordinators:
        return getCurrentUserFilter().concat(shapeEmployeesList(valuationCoordinators));
      case filterTypes.valuationDirectorsInternetUser:
        return getCurrentUserFilter().concat(shapeEmployeesList(valuationDirectors));
      case filterTypes.valuationDirectorsEmployeeId:
        return getValuationDirectors(gridFilterMenus);
      case filterTypes.exportType:
        return shapeExportTypeList(exportTypes);
      case filterTypes.yesNo:
        return yesNo;
      case filterTypes.customerNumbers:
        return getCustomerFilter(gridFilterMenus, activeTemplate);
      case filterTypes.surveyTypes:
        return getSurveyTypeFilter(allSurveyTypes);
      case filterTypes.appraisalStatus:
        return appraisalStatus;
      case filterTypes.states:
        return stateList;
      case filterTypes.testAndTrainingAccounts:
        return testAndTrainingAccounts;
      case filterTypes.fieldStatus:
        return webStatus;
      case filterTypes.hazardLevel:
        return hazards;
      case filterTypes.businessTypes:
        return businessTypes;
      case filterTypes.appraiserManagers:
        return appraiserManagers;
      case filterTypes.activeInactive:
        return activeInactive;
      case filterTypes.assignmentTypes:
        return getAssignmentTypes(gridFilterMenus);
      case filterTypes.fieldDirector:
        return getFieldDirectors(gridFilterMenus);
      case filterTypes.fieldLevelCoordinator:
        return getFieldLevelCoordinators(gridFilterMenus);
      case filterTypes.appraisalSupportRepresentatives:
        return getAppraiserSupportReps(gridFilterMenus);
      case filterTypes.customerServiceAssignUsers:
        return getCurrentUserFilter().concat(customerServiceAssignUsers);
      case filterTypes.surveyClass:
        return inspectionClass;
      case filterTypes.customerType:
        return customerTypeCode;
      case filterTypes.underwriterCode:
        return getCurrentUnderwriterFilter().concat(underwriters);
      case filterTypes.inspectorHiringForecastStatus:
        return inspectorHiringForecastStatus;
      case filterTypes.feedbackCategory:
        return feedbackCategories;
      case filterTypes.qualityAssuranceDirector:
        return busOpsDirectors;
      case filterTypes.qualityAssuranceManager:
        return busOpsManagers;
      case filterTypes.inspectorAssignedSpecificCodes:
        return inspectorSpecificCodes;
      case filterTypes.appraiserAssigned:
        return appraisers;
      case filterTypes.reviewerActions:
        return customerActions;
      case filterTypes.fieldQualityAssuranceAgent:
        return gridFilterMenus?.fieldQualityAssuranceAgents ?? [];
      case filterTypes.appraisalQualityAssuranceAgent:
        return getAppraisalQualityAssuranceAgents(gridFilterMenus);
      case filterTypes.county:
        return countyList;
      case filterTypes.fieldDevelopmentManager:
        return fieldDevelopmentManagers;
      case filterTypes.lenderLogisticsManager:
        return lenderLogisticsManagers;
      case filterTypes.appraisalPortStatus:
        return appraisalPortStatusGroups;
      case filterTypes.serviceRegion:
        return serviceRegions;
      case filterTypes.fieldOperationsManager:
        return operationsManagers;
      case filterTypes.feedbackResults:
        return feedbackResultsOptions;
      case filterTypes.lowMediumHighTiers:
        return lowMediumHighTiers;
      case filterTypes.stackCodes:
        return getStackCodeFilter(mounted);
      case filterTypes.customerServicePersonName:
        return getCustomerServicePersonNames(gridFilterMenus);
      case filterTypes.cubiCasaStatus:
        return cubiCasaStatus;
      case filterTypes.departments:
        return CustomerServiceDeptOptions;
      case filterTypes.customerExperienceManagers:
        return customerExperienceManagers;
      case filterTypes.customerExperienceDirectors:
        return customerExperienceDirectors;
      case filterTypes.fieldRepAssigned:
        return gridFilterMenus.fieldReps?.map(fr => ({ label: fr.firstLast, value: fr.number })) ?? [];
      case filterTypes.satisfactionWithSchedulingProcess:
      case filterTypes.didInspectorArriveOnTimeScore:
      case filterTypes.inspectorsProfessionalismAndCommunicationScore:
      case filterTypes.overallSatisfactionScore:
        return dropDownOneThroughFive;
      default:
        return [];
    }
  }

  quickListEditSelector = () => document.querySelector(".QuickListEdit");
  onSelectChange = mSelectValues => this.MSelectValueChanged(mSelectValues);

  render() {
    const { field, operator, value, handleOnChange, title, simpleMode, inputType, className, disableMSelectPortal } = this.props;
    const { selectOptions, multiValue } = this.state;
    let fieldType = getFieldType(field, operator, simpleMode, inputType);

    switch (fieldType) {
      case filterControls.options:
      case filterControls.singleOptions:
        {
          let isMulti = fieldType === filterControls.options;
          let MSelectValue;
          if (isMulti) MSelectValue = multiValue.filter(v => v.trim() !== "");
          else MSelectValue = value;
          return (<MSelect
            id="ql-mselect"
            options={selectOptions}
            allOption={this.getAllOption()}
            placeholder={this.getPlaceHolder()}
            getMenuPortalTarget={disableMSelectPortal ? undefined : this.quickListEditSelector}
            value={MSelectValue}
            onChange={this.onSelectChange}
            isMulti={isMulti}
            noPadding
            ref={this.selectRef}
            className={className}
          />);
        }

      case filterControls.slider:
        {
          return this.getSlider();
        }

      case filterControls.rangeSlider:
        {
          return (<MSlider
            id="rule-filter-rangeslider"
            className={className}
            key="__mslider-range"
            {...DayRangePlusMinus45}
            defaultValue={multiValue && Array.isArray(multiValue) && multiValue.length == 2 ? multiValue : [0, 7]}
            onChangeCallback={this.multiValueChanged}
          />);
        }
      case null:
        return <div className={className} style={className ? undefined : { width: simpleMode ? 144 : 250, display: "inline-flex" }} />;
    }

    // default case or other issue.
    return (
      <input
        type="text"
        className={className}
        style={{ width: 250 }}
        value={value}
        title={title}
        onChange={e => handleOnChange(e.target.value)} />
    );
  }
}

ValueEditor.displayName = "ValueEditor";

ValueEditor.propTypes = {
  field: PropTypes.string,
  operator: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.array,
    PropTypes.string,
  ]),
  handleOnChange: PropTypes.func,
  onOperatorChanged: PropTypes.func,
  title: PropTypes.string,
  simpleMode: PropTypes.bool,
  disableMSelectPortal: PropTypes.bool,

  gridFilterMenus: PropTypes.object.isRequired,
  stateList: PropTypes.array,
  allSurveyTypes: PropTypes.array,
  appraiserManagers: PropTypes.array,
  customerServiceAssignUsers: PropTypes.array,
  activeTemplate: PropTypes.string,
  inputType: PropTypes.string,
  underwriters: PropTypes.array,
  busOpsDirectors: PropTypes.array,
  busOpsManagers: PropTypes.array,
  appraisers: PropTypes.array,
  customerActions: PropTypes.array,
  valuationCoordinators: PropTypes.array,
  valuationDirectors: PropTypes.array,
  countyList: PropTypes.array,
  exportTypes: PropTypes.array,
  fieldDevelopmentManagers: PropTypes.array,
  lenderLogisticsManagers: PropTypes.array,
  className: PropTypes.string,
  serviceRegions: PropTypes.array,
};

const mapStateToProps = state => {
  return {
    allSurveyTypes: state.user.allSurveyTypes,
    stateList: stateListSelector(state),
    appraiserManagers: appraiserManagersSelector(state),
    gridFilterMenus: state.dashboard.gridFilterMenus,
    customerServiceAssignUsers: customerServiceAssignToUsersSelector(state),
    underwriters: underwritersSelector(state),
    busOpsDirectors: busOpsDirectorsSelector(state),
    busOpsManagers: busOpsManagersSelector(state),
    appraisers: appraisersSelector(state),
    customerActions: customerActionsSelector(state),
    valuationCoordinators: state.appraiser.valuationCoordinators,
    valuationDirectors: state.appraiser.valuationDirectors,
    countyList: allCountiesSelector(state),
    exportTypes: state.exportTypes.exportTypeResults,
    fieldDevelopmentManagers: allFieldDevelopmentManagersSelector(state),
    lenderLogisticsManagers: allLenderLogisticsManagersSelector(state),
    serviceRegions: serviceRegionSelector(state),
    operationsManagers: fieldOperationsManagersSelector(state),
    customerExperienceManagers: allCustomerExperienceManagersSelector(state),
    customerExperienceDirectors: allCustomerExperienceDirectorsSelector(state),
  };
};
export default connect(mapStateToProps)(ValueEditor);
